import { createContext, type ReactNode, useCallback, useContext, useEffect, useState } from 'react';
import { useHoaDonDichVuTraLaiGiamGia } from '@/hooks/queries/useHoaDonDichVuTraLaiGiamGia';
import type { SearchValues } from '@/components/custom/erp/data-table-v2';

interface HoaDonDichVuTraLaiGiamGiaSearchContextValue {
  searchState: {
    filterParams: SearchValues;
    searchResults: BanHangRecord[];
    isSearching: boolean;
  };
  handleSearchChange: (values: SearchValues) => Promise<void>;
  resolvedRows: BanHangRecord[];
}

const HoaDonDichVuTraLaiGiamGiaSearchContext = createContext<HoaDonDichVuTraLaiGiamGiaSearchContextValue | null>(null);

export function useHoaDonDichVuTraLaiGiamGiaSearchContext() {
  const context = useContext(HoaDonDichVuTraLaiGiamGiaSearchContext);
  if (!context) {
    throw new Error(
      'useHoaDonDichVuTraLaiGiamGiaSearchContext must be used within HoaDonDichVuTraLaiGiamGiaSearchProvider'
    );
  }
  return context;
}

export function HoaDonDichVuTraLaiGiamGiaSearchProvider({ children }: { children: ReactNode }) {
  const [searchState, setSearchState] = useState<{
    filterParams: SearchValues;
    searchResults: BanHangRecord[];
    isSearching: boolean;
  }>({
    filterParams: {},
    searchResults: [],
    isSearching: false
  });

  const { filterHoaDonDichVuTraLaiGiamGias } = useHoaDonDichVuTraLaiGiamGia();

  const handleSearchChange = useCallback(async (values: SearchValues) => {
    const nextHasFilters = Object.keys(values || {}).length > 0;

    if (!nextHasFilters) {
      setSearchState({
        filterParams: {},
        searchResults: [],
        isSearching: false
      });
      return;
    }

    setSearchState(prev => ({
      ...prev,
      filterParams: values,
      isSearching: false,
      searchResults: []
    }));
  }, []);

  // Fetch filtered data when filterParams change
  useEffect(() => {
    const fetchFilteredData = async () => {
      if (!searchState.filterParams || Object.keys(searchState.filterParams).length === 0) {
        return;
      }

      setSearchState(prev => ({ ...prev, isSearching: true }));

      try {
        const transformedParams = Object.entries(searchState.filterParams).reduce((acc, [key, value]) => {
          acc[key] = Array.isArray(value) ? value : [value];
          return acc;
        }, {} as BanHangSearchParams);

        const res = await filterHoaDonDichVuTraLaiGiamGias(transformedParams);
        setSearchState(prev => ({
          ...prev,
          searchResults: res || [],
          isSearching: false
        }));
      } catch (error) {
        console.error('Error fetching filtered data:', error);
        setSearchState(prev => ({
          ...prev,
          searchResults: [],
          isSearching: false
        }));
      }
    };

    fetchFilteredData();
  }, [searchState.filterParams, filterHoaDonDichVuTraLaiGiamGias]);

  const resolvedRows = searchState.searchResults;

  return (
    <HoaDonDichVuTraLaiGiamGiaSearchContext.Provider
      value={{
        searchState,
        handleSearchChange,
        resolvedRows
      }}
    >
      {children}
    </HoaDonDichVuTraLaiGiamGiaSearchContext.Provider>
  );
}
